'use strict';

import React, {Component} from "react";
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import {FaExclamationCircle, FaCheck} from "react-icons/fa";

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import ButtonToolbar from "react-bootstrap/ButtonToolbar";

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import Broken from '@uw-it-sis/lib-react/lib/Broken';
import UnknownState from '@uw-it-sis/lib-react/lib/UnknownState';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import termStore from '@uw-it-sis/lib-react/lib/TermStore';

import AddCourseToPlanFormStore from './AddCourseToPlanFormStore';
import {MetricLabels, PLAN_ITEM_NOTE_LIMIT} from './Constants';

const analyticsStore = appStore.getAnalyticsStore();

/**
 * Allows the user to mark their MyPlan plan as Shared or Private.
 */

class AddCourseToPlanForm extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.store = new AddCourseToPlanFormStore();

        // Bind event handlers.
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        this.store.mount(this.props.course);
    }

    componentWillUnmount() {
        // Get rid of the observers defined in the store.
        this.store.unmount();
    }

    /**
     * Callback to the parent component when the cancel link is clicked.
     */
    handleCancelClick = (e) => this.props.onCancel();

    /**
     * Handle input widget changes.
     */
    onChange(e) {
        let property = e.target.name;
        let value = undefined;
        if (property === "backup") {
            value = e.target.checked;
        } else {
            value = e.target.value;
        }
        this.store.updateFormData(property, value);
    }

    /**
     * Handle form submit.
     */
    onSubmit(e) {
        // Prevent a page reload.
        e.preventDefault();
        this.store.addCourse();

        const labels = analyticsStore.label(MetricLabels.quarter, this.store.formData.termId).add(MetricLabels.appContextName, this.props.analyticsContext).get();
        analyticsStore.addEvent('Add-Course', labels,
            {cCode: this.props.course.courseCode});
    }

    _makeForm() {
         //  Build the term options list for the drop-down.
        let termOptions = termStore.get('futureTerms').map(function (term, i) {
            return (<option key={i} value={term.id}>{term.name}</option>);
        });

        let courseSummary = this.props.course;

        //  Build the term badges.
        let scheduledTerms = <span>Not currently offered</span>;
        if (courseSummary.scheduledTerms.length > 0) {
            let scheduledTermList = courseSummary.scheduledTerms.map((term, i) => {
                return (
                    <li className="list-inline-item" key={i}>
                        {TermUtils.makeTermBadge(term, i)}
                    </li>
                );
            });

            scheduledTerms = <ul className="list-inline mb-0">{scheduledTermList}</ul>;
        }
        /*
         * Determine whether to show the buttons or not.
         */
        let buttonsComponent, messageComponent, closeComponent;

        let checkBoxLabel = <span>Mark as <strong>'Backup'</strong> course.</span>;

        // Action was successful, show only messaging
        const termId = this.store.formData.termId;
        const termName = termStore.getTermId(termId, 'futureTerms').name;

        let formComponent =
            <dl className="row description-term-colon mb-0">
                <dt className="col-sm-4">Scheduled for</dt>
                <dd className="col-sm-8">
                    <div className="form-group">{scheduledTerms}</div>
                </dd>

                <dt className="col-sm-4">Add this course to</dt>
                <dd className="col-sm-8">
                    <Form.Group controlId="formControlstermOptions">
                        <Form.Select
                            name="termId"
                            onChange={this.onChange}>
                            {termOptions}
                        </Form.Select>
                        <Form.Text className="mb-2">Note: This course MAY NOT be offered in your selected
                            quarter. Check back after the Time Schedule is released.</Form.Text>
                    </Form.Group>
                </dd>

                <dt className="col-sm-4">Notes</dt>
                <dd className="col-sm-8">
                    <Form.Group controlId="formControlsnote">
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="note"
                            ref="note"
                            onChange={this.onChange}
                            placeholder="Enter a short note"
                            maxLength={PLAN_ITEM_NOTE_LIMIT}/>
                        <Form.Text className="d-flex justify-content-end">{PLAN_ITEM_NOTE_LIMIT} character limit</Form.Text>
                    </Form.Group>
                </dd>
                <dd className="col-12 text-end text-sm-start">
                    <Form.Check
                        id="backupCheckbox"
                        name="backup"
                        checked={this.store.formData.backup}
                        onChange={this.onChange}
                        label={checkBoxLabel} />
                </dd>
            </dl>

        switch (this.store.actionState) {
            case States.pending:
                // Add to plan button gets disabled and shows spinner indicating pending action
                buttonsComponent =
                    <ButtonToolbar className="justify-content-end mt-3">
                        <Button variant="primary" type="submit" disabled>
                            <Spinner animation="border" size="sm" className="me-2 align-middle" aria-hidden="true"/>
                            <span className="align-middle">Add to Plan</span>
                        </Button>
                        <Button variant="secondary" onClick={this.handleCancelClick} type="reset">Cancel</Button>
                    </ButtonToolbar>;
                break;
            case States.error:
                // Buttons remain for user to fix issue and retry
                buttonsComponent =
                    <ButtonToolbar className="justify-content-end mt-3">
                        <Button variant="primary" type="submit">Add to Plan</Button>
                        <Button variant="secondary" onClick={this.handleCancelClick} type="reset">Cancel</Button>
                    </ButtonToolbar>;
                messageComponent =
                    <Alert variant="danger" className="mb-0 mt-3">
                        <FaExclamationCircle className="me-2" aria-hidden="true"/>
                        {this.store.actionErrorMessage}
                    </Alert>;
                break;
            case States.done:
                formComponent = null;
                buttonsComponent = null;
                messageComponent =
                    <Alert variant="success" className="mb-0">
                        <FaCheck className="me-2" aria-hidden="true"/>
                        Added to <a href={"/plan/#/" + TermUtils.makeTermLinkIdentifierFromTermId(this.store.getFormData().termId)}>{termName}</a>
                    </Alert>;
                closeComponent =
                    <ButtonToolbar className="justify-content-end mt-3">
                        <Button variant="primary" onClick={this.handleCancelClick} type="reset">Close</Button>
                    </ButtonToolbar>;
                break;
            default:
                // Initial state with buttons and no messaging
                buttonsComponent =
                    <ButtonToolbar className="justify-content-end mt-3">
                        <Button variant="primary" type="submit">Add to Plan</Button>
                        <Button variant="secondary" onClick={this.handleCancelClick} type="reset">Cancel</Button>
                    </ButtonToolbar>;
                messageComponent = null;
        }



        return (
            <form ref="form" onSubmit={this.onSubmit} className="course-summary">
                {formComponent}
                <div className="mt-3">
                    {buttonsComponent}
                    <div role="alert" aria-live="assertive" aria-atomic="true">
                        {messageComponent}
                    </div>
                    {closeComponent}
                </div>
            </form>
        );
    }

    render() {
        let component;
        switch (this.store.state) {
            case States.initial:
            case States.pending:
                component =
                    <div className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>;
                break;
            case States.done:
                component = this._makeForm();
                break;
            case States.error:
                component = <Broken name="AddCourseToPlanForm"/>;
                break;
            default:
                component = <UnknownState name="AddCourseToPlanForm"/>;
        }
        return (component);
    }
}

AddCourseToPlanForm.propTypes = {
    course: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    analyticsContext: PropTypes.string.isRequired
};

export default observer(AddCourseToPlanForm);